/**
 * The clock of a Mini Planner day: when each item starts and ends, and which
 * ones overlap. The planner shows these times and the agent portal's Send to
 * review books them, so both read them from here.
 */

/** Builder slots, booked at these times when the item has none of its own. */
export const PLANNER_SLOT_CLOCK: Readonly<Record<string, string>> = {
  transfer: '08:30',
  morning: '09:30',
  lunch: '13:00',
  afternoon: '15:30',
  dinner: '20:00',
  hotel: '21:30',
};

/** Each kind's time when it has neither a time nor a slot. */
export const PLANNER_KIND_CLOCK: Readonly<Record<string, string>> = {
  hotel: '21:30',
  restaurant: '13:00',
  experience: '09:30',
};

/** A second restaurant on the day with no time is dinner. */
export const PLANNER_DINNER_CLOCK = '20:00';

/** Minutes between one untimed experience's end and the next one's start. */
export const PLANNER_EXPERIENCE_GAP_MINUTES = 30;
/** An experience whose length is unknown is taken to last this long. */
export const PLANNER_DEFAULT_EXPERIENCE_MINUTES = 120;
/** A meal whose length is unknown is taken to last this long. */
export const PLANNER_DEFAULT_MEAL_MINUTES = 90;

export type PlannerClockItem = {
  kind?: string | null;
  /** HH:MM on the item's day; null: the day's clock places it. */
  startTime?: string | null;
  /** HH:MM the agent set; null: start plus the item's length. */
  endTime?: string | null;
  slot?: string | null;
  durationMinutes?: number | null;
  /** A base hotel's stay: never on the day's clock. */
  nights?: number | null;
};

const CLOCK_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

export const isPlannerClock = (value: unknown): value is string =>
  typeof value === 'string' && CLOCK_RE.test(value);

export const clockToMinutes = (hhmm: string): number => {
  const [hours, minutes] = hhmm.split(':').map((part) => Number(part) || 0);
  return (hours ?? 0) * 60 + (minutes ?? 0);
};

/** Minutes past midnight as HH:MM, kept within the day. */
export const minutesToClock = (minutes: number): string => {
  const bounded = Math.min(Math.max(Math.round(minutes), 0), 23 * 60 + 45);
  const hours = Math.floor(bounded / 60);
  return `${String(hours).padStart(2, '0')}:${String(bounded % 60).padStart(2, '0')}`;
};

const isHotel = (item: PlannerClockItem) =>
  Boolean(item.nights) || item.kind === 'hotel';

const kindOf = (item: PlannerClockItem) => item.kind ?? 'experience';

/**
 * A day's experiences with no time of their own follow each other: the first
 * at the morning clock, each next one after the previous ends plus a gap
 * (to the quarter hour), so two tours are never booked at the same time.
 * Anything else keeps its own time (null when it has none).
 */
export const plannerDayStartTimes = (
  day: readonly PlannerClockItem[]
): (string | null)[] => {
  let cursor = clockToMinutes(PLANNER_KIND_CLOCK.experience as string);
  return day.map((item) => {
    if (item.nights || kindOf(item) !== 'experience') {
      return item.startTime ?? null;
    }
    const own =
      item.startTime ?? (item.slot ? PLANNER_SLOT_CLOCK[item.slot] : undefined);
    const start = own ? clockToMinutes(own) : Math.ceil(cursor / 15) * 15;
    cursor = Math.max(
      cursor,
      start +
        (item.durationMinutes && item.durationMinutes > 0
          ? item.durationMinutes
          : PLANNER_DEFAULT_EXPERIENCE_MINUTES) +
        PLANNER_EXPERIENCE_GAP_MINUTES
    );
    return own ?? minutesToClock(start);
  });
};

/** The time an item is booked at on its day. */
export const plannerItemClock = (item: PlannerClockItem): string =>
  item.startTime ??
  (item.slot ? PLANNER_SLOT_CLOCK[item.slot] : undefined) ??
  PLANNER_KIND_CLOCK[kindOf(item)] ??
  '12:00';

export type PlannerItemSpan = { start: number; end: number };

/** How long an item lasts: its own length, else its kind's usual one. */
export const plannerItemMinutes = (item: PlannerClockItem): number =>
  item.durationMinutes && item.durationMinutes > 0
    ? item.durationMinutes
    : kindOf(item) === 'restaurant'
      ? PLANNER_DEFAULT_MEAL_MINUTES
      : PLANNER_DEFAULT_EXPERIENCE_MINUTES;

/**
 * When each item runs, in minutes past midnight, as it will be booked; null
 * for a hotel, which is not on the day's clock.
 */
export const plannerDaySpans = (
  day: readonly PlannerClockItem[]
): (PlannerItemSpan | null)[] => {
  const starts = plannerDayStartTimes(day);
  return day.map((item, index) => {
    if (isHotel(item)) return null;
    const start = clockToMinutes(
      starts[index] ?? plannerItemClock({ ...item, startTime: null })
    );
    const own = item.endTime ? clockToMinutes(item.endTime) : null;
    const end =
      own !== null && own > start ? own : start + plannerItemMinutes(item);
    return { start, end };
  });
};

/** For each item, the indexes of the day's other items it overlaps. */
export const plannerDayOverlaps = (
  day: readonly PlannerClockItem[]
): number[][] => {
  const spans = plannerDaySpans(day);
  return spans.map((span, index) =>
    span
      ? spans.flatMap((other, at) =>
          other &&
          at !== index &&
          span.start < other.end &&
          other.start < span.end
            ? [at]
            : []
        )
      : []
  );
};

const DINNER_FROM = clockToMinutes('17:00');
const LAST_START = clockToMinutes('22:00');

const spanOf = (item: PlannerClockItem): PlannerItemSpan | null =>
  plannerDaySpans([item])[0] ?? null;

/**
 * The first start from the morning clock where `minutes` fit between the
 * day's booked spans, a gap either side; null when the day has no room.
 */
const firstFreeStart = (
  booked: readonly PlannerItemSpan[],
  minutes: number
): number | null => {
  let start = clockToMinutes(PLANNER_KIND_CLOCK.experience as string);
  const gap = PLANNER_EXPERIENCE_GAP_MINUTES;
  for (;;) {
    if (start > LAST_START) return null;
    const at = start;
    const clash = booked.find(
      (span) => at < span.end + gap && span.start < at + minutes + gap
    );
    if (!clash) return start;
    start = Math.ceil((clash.end + gap) / 15) * 15;
  }
};

/**
 * Gives every item on the day that has no time one of its own, so the time
 * the agent sees is the one booked: a Generate slot keeps its clock, an
 * experience takes the first free time from the morning on, a restaurant is
 * lunch — or dinner when the day already has lunch.
 */
export const withPlannerDayClock = <T extends PlannerClockItem>(
  day: readonly T[]
): T[] => {
  if (day.every((item) => isHotel(item) || item.startTime)) return [...day];
  const chained = plannerDayStartTimes(day);
  const booked = day.flatMap((item) =>
    !isHotel(item) && item.startTime ? [spanOf(item)] : []
  ) as PlannerItemSpan[];
  const meals = day.flatMap((item) =>
    kindOf(item) === 'restaurant' && item.startTime
      ? [clockToMinutes(item.startTime)]
      : []
  );
  return day.map((item, index) => {
    if (isHotel(item) || item.startTime) return item;
    let startTime: string;
    if (item.slot && PLANNER_SLOT_CLOCK[item.slot]) {
      startTime = PLANNER_SLOT_CLOCK[item.slot] as string;
    } else if (kindOf(item) === 'restaurant') {
      const hasLunch = meals.some((at) => at < DINNER_FROM);
      const hasDinner = meals.some((at) => at >= DINNER_FROM);
      startTime =
        hasLunch && !hasDinner
          ? PLANNER_DINNER_CLOCK
          : (PLANNER_KIND_CLOCK.restaurant as string);
    } else {
      const free = firstFreeStart(booked, plannerItemMinutes(item));
      startTime =
        free !== null
          ? minutesToClock(free)
          : (chained[index] ?? plannerItemClock(item));
    }
    const placed = { ...item, startTime };
    if (kindOf(item) === 'restaurant') meals.push(clockToMinutes(startTime));
    const span = spanOf(placed);
    if (span) booked.push(span);
    return placed;
  });
};

/**
 * The day's order: hotels first, then the timed items by their clock; items
 * with no time keep their place after them.
 */
export const sortPlannerDay = <T extends PlannerClockItem>(
  day: readonly T[]
): T[] => {
  const hotels = day.filter(isHotel);
  const timed = day
    .filter((item) => !isHotel(item) && item.startTime)
    .sort(
      (a, b) =>
        clockToMinutes(a.startTime as string) -
        clockToMinutes(b.startTime as string)
    );
  const untimed = day.filter((item) => !isHotel(item) && !item.startTime);
  return [...hotels, ...timed, ...untimed];
};

/** What the planner last booked for an item's time, to spot the agent's change. */
export const plannerTimeTag = (item: PlannerClockItem): string =>
  `${plannerItemClock(item)}-${item.endTime ?? ''}`;
